import React, { useMemo, useState } from 'react'
import { ChartType, CustomTrace, CustomTraceWithSum, SumKeyOperand } from '../../shared/utils/chartModel'
import { AddButton } from '@e3dc-react/shell/Elements/Buttons'
import { getChartColorByIndex } from '@e3dc-react/shell/Libs/chartColors'
import { v4 as uuid } from 'uuid'
import { Box } from '@mui/material'
import useChartTraces, { TraceDetails } from 'src/Hooks/use-chart-traces'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import useAvailableLines from 'src/Libs/system-charts/shared/hooks/useAvailableLines'
import SumItem from './SumItem'
import { isEmpty } from 'lodash'
import { TraceKey } from 'src/Libs/system-charts/shared/config/traceConf'
import { updateTraceWithSumKeys } from '../utils/update-trace-with-sum-keys'

export interface SumsProps {
  chartType: ChartType
  currentTraces: CustomTrace[]
  setCurrentTraces: React.Dispatch<React.SetStateAction<CustomTrace[]>>
  handlePopoverOpen: (traceKey: TraceKey, event?: React.MouseEvent<Element, MouseEvent>) => void
}

const Sums: React.FC<SumsProps> = ({ chartType, currentTraces, setCurrentTraces, handlePopoverOpen }) => {
  const tracesWithSumKeys = useMemo(() => currentTraces.filter(trace => trace.sumKeys) as CustomTraceWithSum[], [currentTraces])
  const { getTraceInfoFromName } = useChartTraces()
  const { t } = useCustomTranslation()
  const [filteredTraceNamesWithDetails, setfilteredTraceNamesWithDetails] = useState<TraceDetails[]>([])
  const availableTraceNames = useAvailableLines()
  const availableTraceNamesWithDetails = useMemo(
    () => availableTraceNames.map(key => getTraceInfoFromName(key)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [availableTraceNames]
  )

  if (isEmpty(filteredTraceNamesWithDetails)) {
    const unitsToFilter = ['%', '°C']
    setfilteredTraceNamesWithDetails(
      availableTraceNamesWithDetails.filter(traceDetails => !unitsToFilter.includes(traceDetails.configuration.unit ?? ''))
    )
  }

  const updateTraceWithSum = ({ traceAlternateKey, keys, operand }: { traceAlternateKey: string; keys: string[]; operand: SumKeyOperand }): void => {
    setCurrentTraces(traces => traces.map(trace => updateTraceWithSumKeys(trace, { traceAlternateKey, keys, operand })))
  }

  const addTraceWithSum = (): void =>
    setCurrentTraces(traces => [
      ...traces,
      {
        sumKeys: [],
        color: getChartColorByIndex(0),
        alternateKey: uuid(),
        key: 'Custom_Sum',
        name: t('common.name'),
      },
    ])

  const removeTraceWithSum = (trace: CustomTrace): void => {
    setCurrentTraces(traces => traces.filter(traceToFind => traceToFind.alternateKey !== trace.alternateKey))
  }

  return (
    <>
      <Box>
        {tracesWithSumKeys.map((trace, index) => (
          <SumItem
            key={`${trace.alternateKey}-${index}`}
            chartType={chartType}
            trace={trace}
            availableTraceNamesWithDetails={filteredTraceNamesWithDetails}
            removeTraceWithSum={removeTraceWithSum}
            updateTraceWithSum={updateTraceWithSum}
            handlePopoverOpen={handlePopoverOpen}
          />
        ))}
      </Box>
      <Box sx={theme => ({ display: 'flex', justifyContent: 'center', paddingBottom: theme.spacing(2) })}>
        <AddButton onClick={() => addTraceWithSum()} />
      </Box>
    </>
  )
}

export default Sums
